package pages.inbound

import api.ApiService
import api.dataclasses.inbound.InboundReceiptDetailed
import components.routing.ProtectedRoutingProps
import components.tableCard.TableCard
import components.verticalDetailsCard.VerticalDetailCard
import ics.materialUI.components.StyledBackDrop
import ics.materialUI.components.autoTable.AutoTable
import ics.materialUI.extensions.xs
import ics.utils.promise.launch
import mui.material.*
import mui.material.styles.TypographyVariant
import mui.system.responsive
import react.FC
import react.create
import react.router.useParams
import react.useEffect
import react.useState

val InboundDetailPage = FC<ProtectedRoutingProps>("InboundDetailPage") { props ->
    // state hooks
    val (errorMsgState, setErrorMsgState) = useState<String?>(null)
    val (dataState, setDataState) = useState<InboundReceiptDetailed?>(null)

    // other hooks
    val params = useParams()

    // vars
    val recordId = params.get("recordId")?.toInt()
    val allowedFields: Array<String> = dataState?.parseOriginalICSFieldNames() ?: arrayOf()

    // requests
    val getDataRequest: () -> Unit = {
        launch {
            val response = ApiService.getInboundOrderDetailed(props.session, props.sessionSetter, recordId!!)

            if (response.success) {
                setErrorMsgState(null)
                setDataState(response.data)

                response.refreshedSession?.let {
                    props.sessionSetter(it)
                }

            } else {
                setErrorMsgState(response.errorMsg ?: "An unexpected error occurred")
            }
        }
    }

    // functions to call after render
    useEffect {
        if (recordId != null && dataState == null && errorMsgState == null) getDataRequest()
    }

    // content container
    Grid {
        container = true
        spacing = responsive(2)

        // error alert
        if (!errorMsgState.isNullOrEmpty()) {
            Grid {
                item = true
                xs = 12

                Alert {
                    severity = AlertColor.error

                    +errorMsgState
                }
            }
        }

        // show data if available as information cards in grid
        if (dataState != null && errorMsgState == null) {
            // Order information card as Grid item
            Grid {
                item = true
                xs = 6

                VerticalDetailCard {
                    title = "Order information"
                    cardData = dataState
                    fieldsSequencedToShow = InboundDetailPageFieldMappings.Order.orderInformationCardHeaders.filter {
                        dataState.parseOriginalICSFieldNames().contains(it)
                    }.toTypedArray()
                    contentFieldMapping = InboundDetailPageFieldMappings.Order.fieldMapping
                }
            }

            // Customer information card as Grid item
            Grid {
                item = true
                xs = 6

                VerticalDetailCard {
                    title = "Customer information"
                    cardData = dataState
                    fieldsSequencedToShow = InboundDetailPageFieldMappings.Order.customerInformationCardHeaders.filter {
                        dataState.parseOriginalICSFieldNames().contains(it)
                    }.toTypedArray()
                    contentFieldMapping = InboundDetailPageFieldMappings.Order.fieldMapping
                }
            }

            // Status information card as Grid item
            Grid {
                item = true
                xs = 6

                VerticalDetailCard {
                    title = "Status information"
                    cardData = dataState
                    fieldsSequencedToShow = InboundDetailPageFieldMappings.Order.statusInformationCardHeaders.filter {
                        dataState.parseOriginalICSFieldNames().contains(it)
                    }.toTypedArray()
                    contentFieldMapping = InboundDetailPageFieldMappings.Order.fieldMapping
                }
            }

            // Extra information card as Grid item
            Grid {
                item = true
                xs = 6

                VerticalDetailCard {
                    title = "Extra information"
                    cardData = dataState
                    fieldsSequencedToShow = InboundDetailPageFieldMappings.Order.extraInformationCardHeaders.filter {
                        dataState.parseOriginalICSFieldNames().contains(it)
                    }.toTypedArray()
                    contentFieldMapping = InboundDetailPageFieldMappings.Order.fieldMapping
                }
            }

            // Orderlines card
            Grid {
                item = true
                xs = 12

                TableCard {
                    title = "Orderlines"

                    AutoTable {
                        tableData = dataState.lines
                        fieldsSequencedToShow = InboundDetailPageFieldMappings.OrderLines.headerSequence.filter {
                            dataState.parseOriginalICSFieldNames().contains(it)
                        }.toTypedArray()
                        headers = InboundDetailPageFieldMappings.OrderLines.fieldMapping
                        limit = 25
                        limitOptions = arrayOf(25, 50, 100)
                        search = true
                        dense = true
                    }
                }
            }
        } else StyledBackDrop()
    }
}